import { ArrowRight, Check, Lock, Loader2, ShieldCheck } from 'lucide-react';
import warriorTradingLogo from '../../assets/brand/tenants/warrior-trading/app/logo-horizontal-split-dark.svg';
import { resolveTenantProductName, type WhiteLabelAppBranding } from '../../lib/whiteLabelBranding';

type AbandonedCheckoutPageProps = {
  tenantName?: string;
  productName?: string;
  monthlyPrice?: string;
  trialDays?: number;
  logoSrc?: string | null;
  whiteLabel?: WhiteLabelAppBranding | null;
  onStartCheckout: () => void;
  onSignInToExistingAccount?: () => void;
  tenantLandingUrl?: string;
  isLoading?: boolean;
};

export function AbandonedCheckoutPage({
  tenantName = resolveTenantProductName(),
  productName = resolveTenantProductName(),
  monthlyPrice = '$39',
  trialDays = 7,
  logoSrc,
  whiteLabel,
  onStartCheckout,
  onSignInToExistingAccount,
  tenantLandingUrl: _tenantLandingUrl = '/',
  isLoading = false,
}: AbandonedCheckoutPageProps) {
  const resolvedTenantName = whiteLabel?.companyName || tenantName;
  const resolvedProductName = whiteLabel?.shortProductName || productName;
  const resolvedMonthlyPrice = whiteLabel
    ? `$${whiteLabel.partnerMonthlyPrice}`
    : monthlyPrice;
  const displayProductName = (() => {
    const normalizedProductName = String(resolvedProductName || '').trim();
    if (normalizedProductName === 'Warrior Trading') {
      return 'Warrior Trading Journaling';
    }

    return normalizedProductName || resolveTenantProductName();
  })();

  const displayPrice = String(resolvedMonthlyPrice || '').trim();
  const priceAmount = displayPrice.startsWith('$') ? displayPrice.slice(1) : displayPrice;
  const normalizedTrialDays = Number.isFinite(Number(trialDays))
    ? Math.max(0, Math.trunc(Number(trialDays)))
    : 7;
  const resolvedLogoSrc = whiteLabel?.logoHref || whiteLabel?.logoLightHref || logoSrc || warriorTradingLogo;
  const isWarriorTradingBrand = [resolvedTenantName, resolvedProductName, resolvedLogoSrc].some((value) =>
    String(value || '')
      .toLowerCase()
      .includes('warrior')
  );
  const logoSizeClass = isWarriorTradingBrand ? 'h-20 md:h-24' : 'h-16 md:h-20';

  const handleSignInToExistingAccount = () => {
    if (onSignInToExistingAccount) {
      onSignInToExistingAccount();
      return;
    }

    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b1220] via-[#0f172a] to-[#020617] p-4 font-sans text-slate-200 selection:bg-blue-500/30 flex items-center justify-center">
      <div className="absolute top-0 left-0 h-full w-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] h-96 w-96 rounded-full bg-blue-500/10 blur-3xl opacity-30" />
        <div className="absolute bottom-[-10%] right-[-10%] h-96 w-96 rounded-full bg-indigo-500/10 blur-3xl opacity-30" />
      </div>

      <div className="relative z-10 flex w-full max-w-[720px] flex-col items-center py-8">
        <div className="w-full overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-900/85 shadow-2xl backdrop-blur-xl">
          <div aria-hidden="true" className="h-1 w-full bg-gradient-to-r from-blue-800 via-green-200 to-green-600" />

          <div className="px-6 py-8 md:px-10 md:py-10">
            <div className="mb-6 text-center">
              <div className="mx-auto -mt-1 mb-7">
                <img
                  src={resolvedLogoSrc}
                  alt={resolvedTenantName}
                  className={`mx-auto ${logoSizeClass} w-auto object-contain`}
                />
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-200">
                <ShieldCheck className="h-3.5 w-3.5" />
                Trial Activation
              </div>
            </div>

            <div className="mb-8 text-center">
              <h1 className="text-[34px] font-bold leading-tight tracking-tight text-white md:text-[32px]">
                Start improving your trading today — <br />
                <span className="text-blue-400">free for 7 days</span>
              </h1>
              <p className="mt-3 px-2 text-[16px] text-slate-400">
                Unlock full access to our premium tools and analytics.
              </p>
            </div>

            <div className="relative mx-auto mb-10 mt-6 w-full max-w-[500px] rounded-2xl bg-gradient-to-b from-blue-500/50 via-slate-700/50 to-transparent p-[1px] shadow-[0_0_40px_-10px_rgba(59,130,246,0.3)]">
              <div className="relative overflow-hidden rounded-2xl bg-[#0d1527] px-6 py-10 text-center">
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-blue-500/10 to-transparent" />

                <div className="relative">
                  <h2 className="mb-3 text-xs font-black uppercase tracking-[0.25em] text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-cyan-300 drop-shadow-sm">
                    {displayProductName}
                  </h2>

                  <div className="flex items-start justify-center">
                    <span className="mt-2.5 text-2xl leading-none font-bold text-white opacity-80">$</span>
                    <span className="text-[72px] leading-none font-bold tracking-tight text-white drop-shadow-lg">
                      {priceAmount || '39'}
                    </span>
                    <span className="mb-2 ml-1 self-end text-sm font-semibold text-slate-400">
                      /month
                    </span>
                  </div>

                  <p className="relative mt-4 px-4 text-[14px] font-normal text-slate-400">
                    Used by <span className="font-bold text-slate-300">{resolvedTenantName}</span> members to{' '}
                    <span className="font-bold text-slate-300">review and improve</span> trades
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-10 space-y-4 px-2">
              <div className="mb-2 px-1 text-[12px] font-bold uppercase tracking-widest text-slate-500">
                What&apos;s included:
              </div>

              {[
                {
                    title: 'Replay Sessions',
                  desc: 'Replay every trade and see exactly where you went wrong.',
                  val: '$99/mo value',
                },
                {
                  title: 'Advanced Analytics',
                  desc: 'Spot what’s killing your performance — fast.',
                  val: '$49/mo value',
                },
                {
                  title: 'Execution-Level Insights',
                  desc: 'Understand why you win or lose — no guessing.',
                  val: 'Invaluable',
                },
              ].map((feature) => (
                <div key={feature.title} className="group flex items-start justify-between gap-3 px-1">
                  <div className="flex gap-4">
                    <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20">
                      <Check className="h-3 w-3 text-emerald-400" />
                    </div>

                    <div className="flex flex-col">
                      <span className="text-base font-normal text-slate-200 transition-colors group-hover:text-white">
                        {feature.title}
                      </span>
                      <span className="mt-0.5 pr-2 text-sm font-normal leading-relaxed text-slate-500">
                        {feature.desc}
                      </span>
                    </div>
                  </div>

                  <span className="ml-2 mt-1 whitespace-nowrap text-sm text-slate-500 line-through">
                    {feature.val}
                  </span>
                </div>
              ))}
            </div>

            <div className="w-full space-y-4">
              <div className="flex flex-col rounded-lg border border-slate-800 bg-slate-950/50 p-4 shadow-inner">
                <div className="flex items-center justify-between">
                  <span className="text-base font-semibold text-slate-400">Total Due Today</span>
                  <span className="text-2xl font-black text-white">$0.00</span>
                </div>
                <div className="mt-1 text-right text-[11px] font-medium text-slate-500">
                  You won’t be charged until your trial ends
                </div>
              </div>

              <p className="pt-1 text-center text-[15px] font-medium text-slate-300">
                Pick up where you left off — takes less than 30 seconds
              </p>

              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center text-[12px] text-slate-400 sm:text-[13px]">
                <span className="inline-flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-400" />
                  <span>{normalizedTrialDays}-day free trial — no charge today</span>
                </span>
                <span className="hidden text-slate-600 sm:block">•</span>
                <span className="inline-flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-400" />
                  <span>Cancel anytime in seconds</span>
                </span>
                <span className="hidden text-slate-600 sm:block">•</span>
                <span className="inline-flex items-center gap-1.5">
                  <Check className="h-4 w-4 text-emerald-400" />
                  <span>Secure Stripe checkout</span>
                </span>
              </div>

              <button
                type="button"
                onClick={onStartCheckout}
                disabled={isLoading}
                aria-busy={isLoading}
                className="group relative flex w-full items-center justify-center rounded-lg bg-blue-500 px-4 py-4 text-lg font-bold text-white shadow-[0_4px_20px_-4px_rgba(59,130,246,0.5)] transition-all duration-200 hover:scale-[1.01] hover:bg-blue-600 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b1220]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    <span>Opening checkout…</span>
                  </>
                ) : (
                  <>
                    <span>Continue Secure Checkout</span>
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>

              <div className="pt-4 text-center">
                <button
                  type="button"
                  onClick={handleSignInToExistingAccount}
                  className="text-sm font-semibold text-blue-400 transition-colors hover:text-blue-300 hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b1220]"
                >
                  Sign in to an existing account
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col items-center gap-4 opacity-50">
          <div className="flex items-center gap-2">
            <Lock className="h-3 w-3 text-slate-400" />
            <span className="text-[10px] font-medium uppercase tracking-widest text-slate-400">
              Secure Checkout powered by Stripe
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
